<template>
  <v-card
    v-if="showWidget"
    class="mb-6 mx-0 px-5 pt-5"
    :class="{'pb-16': loadingBase}"
  >
    <SalesRecommendationCommoditySettings
      v-if="!loadingBase && !isSmartAdvisorSelected"
      :value="commoditySettingsOpen"
      :crop-year="currentCropYear"
      @input="commoditySettingsOpen = false"
      @change="updateSelectableCommodities"
    />

    <div
      class="d-flex justify-space-between"
      :class="$isMobile ? 'flex-column' : 'flex-row'"
    >
      <div
        class="d-flex flex-row justify-space-between align-content-center"
      >
        <span
          class="primary-grey--text text-h6 font-weight-bold"
        >
          {{ title }}
        </span>

        <v-btn
          v-if="!isCommercial && !isSmartAdvisorSelected && $isMobile"
          class="pb-1"
          icon
          :disabled="loadingBase"
          @click="openCommoditySettings"
        >
          <v-icon
            medium
          >
            mdi-cog
          </v-icon>
        </v-btn>
      </div>

      <div
        class="d-flex flex-row"
        :class="$isMobile ? 'justify-start ml-n3' : 'justify-end'"
      >
        <span
          v-if="!isCommercial"
          style="max-width: 70%"
        >
          <v-select
            v-model="recommendationPreference"
            :items="recommendationPreferences"
            class="text-body-2 font-weight-medium primary-color"
            :disabled="loadingBase || loadingRecommendations"
            solo
            flat
            dense
            single-line
            hide-details
            @change="onPreferenceSelect"
          />
        </span>

        <v-btn
          v-if="!isCommercial && !isSmartAdvisorSelected && !$isMobile"
          icon
          :disabled="loadingBase"
          @click="openCommoditySettings"
        >
          <v-icon
            medium
          >
            mdi-cog
          </v-icon>
        </v-btn>
      </div>
    </div>

    <v-row
      v-if="!showCTA"
      class="px-0 mt-2"
      :class="$isMobile ? 'ml-n5' : 'mx-n7'"
    >
      <v-col
        :cols="$isMobile ? '8' : '4'"
        class="py-0 px-0 mx-0"
        no-gutters
      >
        <v-select
          v-model="selectedYear"
          :disabled="loadingBase"
          :items="selectableYears"
          :class="$isMobile ? 'ml-2' : 'ml-4'"
          :append-icon="selectableYears.length === 1 ? '' : 'mdi-menu-down'"
          :readonly="selectableYears.length === 1"
          class="font-weight-bold"
          flat
          solo
          item-text="long_name"
          item-value="id"
          return-object
        />
      </v-col>
    </v-row>
    <div class="mb-2 mt-0 justify-end">
      <v-chip-group
        v-if="!(loadingRecommendations || loadingBase)"
        v-model="selectedCommodity"
        mandatory
        column
        active-class="primary"
      >
        <v-chip
          v-for="item,index in sortedSelectableCommodities"
          :key="index"
          :value="item"
        >
          {{ item.display_name }}
        </v-chip>
      </v-chip-group>
    </div>
    <template
      v-if="loadingRecommendations === true || loadingBase === true"
    >
      <div class="grey--text text-center py-16">
        Loading...
      </div>
    </template>
    <sales-recommendations-c-t-a v-else-if="showCTA" />
    <div
      v-else-if="isSmartAdvisorSelected && smartAdvisorLoadAttempted && !combinedCommoditiesString"
      class="primary-grey--text pb-16"
    >
      No commodities added
    </div>
    <div
      v-else
      class="mx-n10"
    >
      <NewSalesRecommendations
        :selected-year="selectedYear"
        :selected-commodity="selectedCommodity"
        :is-smart-advisor-selected="isSmartAdvisorSelected"
        :best-sale-data="cachedBestSaleData[selectedYear.id]"
        :action-plan-data="cachedActionPlanData[selectedYear.id] || {}"
      />
    </div>
  </v-card>
</template>
<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import SalesRecommendationsCTA from '../farm_profile/SalesRecommendationsCTA.vue';
import NewSalesRecommendations from './NewSalesRecommendations.vue';
import SalesRecommendationCommoditySettings from './SalesRecommendationCommoditySettings.vue';

export default {
  name: 'NewSalesRecommendationsWidget',

  components: {
    SalesRecommendationsCTA,
    SalesRecommendationCommoditySettings,
    NewSalesRecommendations,
  },

  data() {
    return {
      title: 'Sales recommendations',
      loadingBase: true,
      loadingRecommendations: false,
      cropData: [],
      recommendationPreference: 'analyst',
      selectedCommodity: {},
      selectedCropYearInternal: null,
      commoditySettingsOpen: false,
      selectableCommodities: [],
      cachedBestSaleData: {},
      cachedActionPlanData: {},
      cachedSmartAdvisorCommodities: {},
      smartAdvisorLoadAttempted: false,
    };
  },

  watch: {
    selectedYear: {
      async handler() {
        if (!this.isSmartAdvisorSelected || !this.selectedYear) { return; }

        if (this.shouldLoadSmartAdvisorData) {
          await this.loadSmartAdvisorCropYearData();
        }
        this.selectableCommodities = this.cachedSmartAdvisorCommodities[this.selectedYear.id];
      },
    },
    isSmartAdvisorSelected: {
      async handler(value) {
        if (!value || !this.selectedYear) { return; }

        if (this.shouldLoadSmartAdvisorData) {
          await this.loadSmartAdvisorCropYearData();
        }
        this.selectableCommodities = this.cachedSmartAdvisorCommodities[this.selectedYear.id];
      },
    },
  },

  computed: {
    ...mapState('analysisDashboard', ['advancedAnalysisCommodities', 'salesRecommendations']),
    ...mapGetters('analysisDashboard', ['GFToAADCommodityMapping']),
    ...mapGetters('permissions', ['checkPermission']),
    ...mapGetters('subscription', ['isDeputter', 'isSyngenta', 'isAmerican', 'isCanadian']),
    ...mapGetters('chargebee', ['isCommercialBasic', 'isCommercial', 'isMarketIntelligence', 'isSmart']),
    ...mapGetters('smartAdvisor', ['commoditiesAvailableForOnboarding', 'productionsSorted', 'bestSaleData', 'actionPlanData', 'isCurrentContext']),
    ...mapState('farmProfile/salesRecommendations', ['farmProfileCommoditiesList']),
    ...mapState('shared', ['cropYears']),
    ...mapGetters('shared', ['currentCropYear', 'nextCropYear']),
    ...mapState('user', ['isExternalUser']),
    ...mapState('launchDarkly', ['flags']),

    years() {
      return this.cropYears.slice(0, 2);
    },

    /**
     * The chip list is fed either an array (analyst preference) or an object keyed by index
     * (the cached Smart Advisor commodities), so normalize before sorting alphabetically.
     */
    sortedSelectableCommodities() {
      return Object.values(this.selectableCommodities || {})
        .filter(Boolean)
        .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));
    },
    recommendationPreferences() {
      const ANALYST = { text: 'BY ANALYST', value: 'analyst' };
      const SMART = { text: 'BY SMART ADVISOR', value: 'smart-advisor' };

      if (this.flags['us-smart-plan']) {
        if (this.isAmerican && this.isSmart) {
          return [SMART];
        }
      } else if (this.isAmerican && this.isMarketIntelligence) {
        return [SMART];
      }
      return [ANALYST, SMART];
    },

    selectableYears() {
      if (this.loadingBase) { return this.years; }

      if (this.isSmartAdvisorSelected) {
        const nextCropYearStartDate = new Date(this.nextCropYear.start);
        const nextCropYearStartYear = nextCropYearStartDate.getFullYear();

        const availabilityDate = new Date(nextCropYearStartYear, 1, 1);
        const currentDate = new Date();

        return this.years.filter((year, index) => !(index === 0 && currentDate < availabilityDate));
      }

      if (!Array.isArray(this.selectableCommodities)) { return []; }

      return this.years.filter((year) => this.salesRecommendations.some((rec) => rec.crop_year_id === year.id
        && this.selectableCommodities.some((commodity) => rec.commodity_id === commodity.id)));
    },

    selectedYear: {
      get() {
        if (this.selectedCropYearInternal) return this.selectedCropYearInternal;
        return this.currentCropYear;
      },
      set(value) {
        this.selectedCropYearInternal = value;
      },
    },

    showCTA() {
      const hasUSFullAccess = this.isAmerican && (this.flags['us-smart-plan'] ? this.isSmart : this.isMarketIntelligence);

      if (hasUSFullAccess) return false;

      return (
        (!this.checkPermission('sales_recommendation', 'read') || this.isCommercialBasic)
        && !(this.recommendationPreference === 'analyst' && this.isMarketIntelligence)
      );
    },

    shouldLoadSmartAdvisorData() {
      return (
        this.isSmartAdvisorSelected
        && this.selectedYear
        && !this.showCTA
        && !this.smartAdvisorDataCached
        && !this.isCommercial
      );
    },

    showWidget() {
      const hasUSFullAccess = this.isAmerican && (this.flags['us-smart-plan'] ? this.isSmart : this.isMarketIntelligence);
      return hasUSFullAccess || (this.isCanadian && !(this.isDeputter || this.isSyngenta));
    },

    isSmartAdvisorSelected() {
      return this.recommendationPreference === 'smart-advisor';
    },

    smartAdvisorCommodities() {
      return Object.values(this.productionsSorted)
        .filter((production) => this.commoditiesAvailableForOnboarding[production.commodity])
        .map((production) => this.commoditiesAvailableForOnboarding[production.commodity]);
    },

    combinedCommoditiesString() {
      return Object.values(this.smartAdvisorCommodities).map((x) => x.name.replace(/ /g, '_')).join(' ');
    },

    selectedBestSaleData() {
      return this.cachedBestSaleData[this.selectedYear.id];
    },

    selectedSmartAdvisorCommodities() {
      return this.cachedSmartAdvisorCommodities[this.selectedYear.id];
    },

    smartAdvisorDataCached() {
      const bestSaleDataCached = !!this.selectedBestSaleData;
      const productionsCached = !!this.selectedSmartAdvisorCommodities;

      return bestSaleDataCached && productionsCached;
    },
  },

  async mounted() {
    this.loadingBase = true;

    try {
      if (this.isCommercial) {
        this.recommendationPreference = 'analyst';
      } else if (this.isAmerican) {
        this.recommendationPreference = 'smart-advisor';
      } else {
        this.recommendationPreference = localStorage.getItem('dashboardRecommendationPreference') || 'analyst';
      }

      this.setSalesRecommendationsTab(!this.showWidget);

      // This should be set to 'next' when the next crop year is selected and
      // next crop year's recommendations are available
      this.setActiveContextKey('current');

      const [_, { commodities }] = await Promise.all([
        this.getStaticData(),
        API.readCommoditiesAndProvinces(),
      ]);

      if (this.shouldLoadSmartAdvisorData) {
        await this.loadSmartAdvisorCropYearData();
      }
      this.cropData = commodities;

      if (this.recommendationPreference === 'smart-advisor') {
        this.selectableCommodities = this.smartAdvisorCommodities;
      }
    } catch (error) {
      this.$snackbar.error(error);
    } finally {
      this.smartAdvisorLoadAttempted = true;
      this.loadingBase = false;
    }
  },

  methods: {
    ...mapActions('shared', ['fetchAllCropYears', 'fetchAllUnits']),
    ...mapActions('analysisDashboard', [
      'fetchAllSalesRecommendations',
      'fetchAnalysisDashboardCommodities',
      'fetchPriceMapLinks',
    ]),
    ...mapActions('dashboard', ['setSalesRecommendationsTab']),
    ...mapActions('smartAdvisor', [
      'fetchAADCommodities',
      'getBestSaleData',
      'getActionPlanData',
      'getSupportedParameters',
      'getSmartAdvisorProductions',
      'getPersonalizationDataByCropYearId']),
    ...mapActions('farmProfile/salesRecommendations', ['getFarmprofileCommodities']),
    ...mapMutations('smartAdvisor', ['setActiveContextKey']),

    async getStaticData() {
      await Promise.all([
        this.fetchAllCropYears(),
        this.fetchAllUnits(),
        this.fetchAnalysisDashboardCommodities(),
        this.fetchAllSalesRecommendations(),
        this.fetchPriceMapLinks(),
        this.getFarmprofileCommodities(),
      ]);
    },

    openCommoditySettings() {
      this.commoditySettingsOpen = true;
    },

    onPreferenceSelect(value) {
      if (value === 'smart-advisor') {
        this.selectableCommodities = this.smartAdvisorCommodities;
      }
      localStorage.setItem('dashboardRecommendationPreference', value);

      // Always switch to defaults due to the different number of available years between preferences
      this.selectedYear = this.currentCropYear;
    },

    updateSelectableCommodities(commodities) {
      this.selectableCommodities = commodities;
    },

    async loadSmartAdvisorCropYearData() {
      this.loadingRecommendations = true;
      this.setActiveContextKey(this.selectedYear?.id === this.currentCropYear?.id ? 'current' : 'next');
      await this.fetchAADCommodities();
      await this.getSmartAdvisorProductions();
      await this.getSupportedParameters();

      try {
        await Promise.all([
          this.getPersonalizationDataByCropYearId(this.selectedYear?.id),
        ]);

        if (this.combinedCommoditiesString) {
          const saleDataRequests = [this.getBestSaleData({ commodities: this.combinedCommoditiesString })];
          if (!this.isCurrentContext) {
            saleDataRequests.push(this.getActionPlanData({ commodities: this.combinedCommoditiesString }));
          }
          await Promise.all(saleDataRequests);
        }
      } catch (e) {
        this.$snackbar.error(e);
      }

      this.cachedBestSaleData[this.selectedYear.id] = { ...this.bestSaleData };
      this.cachedActionPlanData[this.selectedYear.id] = { ...this.actionPlanData };
      this.cachedSmartAdvisorCommodities[this.selectedYear.id] = { ...this.smartAdvisorCommodities };
      this.loadingRecommendations = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/colors/colors";

::v-deep .v-text-field__details .v-select__selections input {
  display: none !important;
}

.primary-color::v-deep .v-select__selections {
  color: colors.$primary !important;
}
</style>
